import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import InfoTooltip from '../../../components/ui/InfoTooltip';

const VALUES_HINT =
  'Valeurs propres à ce prospect. Les libellés de champs sont communs à toute l’entreprise.';

const ADD_FIELD_HINT =
  'Réservé à la gestion CRM : le libellé est ajouté pour tous les prospects (sans copier de valeur).';

const ProspectCustomFieldsEditor = ({
  rows,
  onChange,
  canManageDefinitions = false,
  onAddDefinition,
  addingDefinition = false,
}) => {
  const list = Array.isArray(rows) ? rows : [];
  const [newLabel, setNewLabel] = useState('');

  const updateRow = (id, patch) => {
    onChange(list.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const submitNewDefinition = async () => {
    const label = newLabel.trim();
    if (!label || !onAddDefinition) return;
    await onAddDefinition(label);
    setNewLabel('');
  };

  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium text-foreground">Champs personnalisés</p>
        <InfoTooltip text={VALUES_HINT} />
      </div>

      {list.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Aucun champ défini pour votre entreprise.
          {canManageDefinitions
            ? ' Ajoutez un libellé ci-dessous pour l’ouvrir sur toutes les fiches.'
            : ' Contactez un responsable gestion CRM pour en créer.'}
        </p>
      ) : (
        <div className="space-y-2">
          {list.map((row) => (
            <div
              key={row.id}
              className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-2 items-start"
            >
              <div className="text-sm pt-2 md:pt-7">
                <span className="font-medium text-foreground">{row.label}</span>
              </div>
              <Input
                label="Valeur"
                value={row.value}
                onChange={(e) => updateRow(row.id, { value: e.target.value })}
                labelTooltip="Contenu pour ce prospect uniquement"
              />
            </div>
          ))}
        </div>
      )}

      {canManageDefinitions ? (
        <div className="space-y-2 border-t border-border pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-medium text-foreground">Nouveau champ entreprise</p>
            <InfoTooltip text={ADD_FIELD_HINT} />
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1 min-w-0">
              <Input
                label="Libellé du champ"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void submitNewDefinition();
                  }
                }}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconName="Plus"
              loading={addingDefinition}
              disabled={addingDefinition || !newLabel.trim()}
              onClick={() => void submitNewDefinition()}
            >
              Ajouter pour tous
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export function ProspectCustomFieldsDisplay({ fields, className = '' }) {
  const list = Array.isArray(fields)
    ? fields.filter((f) => String(f?.label ?? '').trim())
    : [];
  const withValue = list.filter((f) => String(f?.value ?? '').trim());
  if (!withValue.length) return null;

  return (
    <div className={`space-y-1.5 pt-2 border-t border-border/60 ${className}`.trim()}>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Champs personnalisés
      </p>
      {withValue.map((f, i) => (
        <div key={`${f.fieldId || f.label}-${i}`} className="flex items-start gap-2 text-sm">
          <Icon name="Tag" size={14} className="text-muted-foreground mt-0.5 shrink-0" />
          <div className="min-w-0">
            <span className="text-muted-foreground">{f.label} : </span>
            <span className="text-foreground break-words">{f.value}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default ProspectCustomFieldsEditor;
